import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useOrganizations } from '../contexts/OrganizationsContext'
import emailStatsService from '../services/emailStatsService'
import { 
  Crown, 
  Shield, 
  Activity, 
  Zap, 
  Settings, 
  Users, 
  FileText, 
  BarChart3, 
  Download,
  Building,
  AlertTriangle,
  CheckCircle,
  Clock,
  Plus,
  X
} from 'lucide-react'
import toast from 'react-hot-toast'
import { 
  collection, 
  query, 
  getDocs, 
  getDoc,
  orderBy, 
  doc, 
  updateDoc, 
  serverTimestamp,
  Timestamp,
  where
} from 'firebase/firestore'
import { db } from '../services/firebase'
import adminService from '../services/adminService'
import SpecialAccessManager from '../components/SpecialAccessManager'

export default function PlatformCreator() {
  const { currentUser, userProfile } = useAuth()
  const { organizations } = useOrganizations()
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false)
  const [organizationRequests, setOrganizationRequests] = useState([])
  const [requestsLoading, setRequestsLoading] = useState(false)
  const [showSpecialAccessModal, setShowSpecialAccessModal] = useState(false)
  const [adminOrganizations, setAdminOrganizations] = useState([])
  const [isOrgAdmin, setIsOrgAdmin] = useState(false)
  const [platformStats, setPlatformStats] = useState({
    totalAlerts: 0,
    totalEmails: 0,
    totalUsers: 0,
    totalOrganizations: 0,
    pendingRequests: 0,
    emailStats: {
      totalEmails: 0,
      alertEmails: 0,
      organizationRequestEmails: 0,
      testEmails: 0,
      otherEmails: 0
    }
  })
  const [statsLoading, setStatsLoading] = useState(false)
  const [timePeriod, setTimePeriod] = useState('total') // 'day', 'week', 'month', 'year', 'total'

  // Platform admin UIDs - Only Jed (Platform Creator)
  const platformAdminUIDs = ['YnFxDZkOteWCNr49v49g9AVqA9m2']
  const platformAdminEmails = ['jed@chimeo.app']

  // Check if user is platform admin
  const checkPlatformAdminStatus = () => {
    // Auto-add jed@chimeo.app UID if not in list
    if (currentUser?.email === 'jed@chimeo.app' && currentUser?.uid && !platformAdminUIDs.includes(currentUser.uid)) {
      console.log('🔧 Auto-adding jed@chimeo.app UID to platform admin list:', currentUser.uid)
      platformAdminUIDs.push(currentUser.uid)
    }
    
    const isAdmin = (currentUser?.email && platformAdminEmails.includes(currentUser.email)) ||
                   (currentUser?.uid && platformAdminUIDs.includes(currentUser.uid))
    setIsPlatformAdmin(isAdmin)
    return isAdmin
  }

  // Grant platform admin access to all organizations
  const grantPlatformAdminOrgAccess = async () => {
    if (!currentUser?.uid) {
      console.log('❌ No current user found')
      toast.error('No user found. Please log in again.')
      return
    }
    
    try {
      console.log('🔧 Granting platform admin access...')
      
      // Update user profile to be organization admin
      const userRef = doc(db, 'users', currentUser.uid)
      await updateDoc(userRef, {
        isOrganizationAdmin: true,
        platformAdmin: true,
        updatedAt: serverTimestamp()
      })
      console.log('✅ User profile updated')
      
      // Get all organizations and add platform admin to each
      const orgsQuery = query(collection(db, 'organizations'))
      const orgsSnapshot = await getDocs(orgsQuery)
      console.log(`✅ Found ${orgsSnapshot.docs.length} organizations`)
      
      for (const orgDoc of orgsSnapshot.docs) {
        const orgData = orgDoc.data()
        const adminIds = orgData.adminIds || {}
        const adminRoles = orgData.adminRoles || {}
        
        if (!adminIds[currentUser.uid]) {
          adminIds[currentUser.uid] = true
          adminRoles[currentUser.uid] = 'organization_admin'
          
          await updateDoc(orgDoc.ref, {
            adminIds,
            adminRoles,
            updatedAt: serverTimestamp()
          })
          
          // console.log(`✅ Added to: ${orgData.name}`)
        }
      }
      
      console.log('✅ Platform admin access granted!')
      // toast.success('Platform admin access granted!')
      
      // Reload admin organizations
      await loadAdminOrganizationsFromFirestore()
      
    } catch (error) {
      console.error('❌ Error granting platform admin access:', error)
      console.error('❌ Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      })
      toast.error(`Failed to grant platform admin access: ${error.message}`)
    }
  }

  // Load admin organizations directly from Firestore
  const loadAdminOrganizationsFromFirestore = async () => {
    try {
      console.log('🔍 Loading admin organizations directly from Firestore...')
      console.log('🔍 Current user UID:', currentUser?.uid)
      
      // Check if user is platform admin first
      const isPlatformAdminUser = platformAdminUIDs.includes(currentUser?.uid)
      
      if (isPlatformAdminUser) {
        console.log('🔧 Platform Creator detected - loading all organizations for management')
        
        const orgsQuery = query(collection(db, 'organizations'))
        const orgsSnapshot = await getDocs(orgsQuery)
        
        const adminOrgs = []
        
        for (const orgDoc of orgsSnapshot.docs) {
          const orgData = orgDoc.data()
          const orgId = orgDoc.id
          
          // Platform creator has management access to ALL organizations
          adminOrgs.push({
            id: orgId,
            name: orgData.name,
            userRole: 'platform_creator',
            canManageAdmins: true,
            platformAdmin: true,
            platformCreator: true
          })
          
          console.log(`✅ Platform creator management access to: ${orgData.name}`)
        }
        
        console.log('🔍 Total organizations (platform creator):', adminOrgs.length)
        setAdminOrganizations(adminOrgs)
        setIsOrgAdmin(false) // Platform creator is NOT an org admin, but has platform access
        return adminOrgs
      } else {
        // Regular user logic
        const orgsQuery = query(collection(db, 'organizations'))
        const orgsSnapshot = await getDocs(orgsQuery)
        
        const adminOrgs = []
        
        for (const orgDoc of orgsSnapshot.docs) {
          const orgData = orgDoc.data()
          const orgId = orgDoc.id
          
          // Check if current user is in adminIds
          const adminIds = orgData.adminIds || {}
          const adminRoles = orgData.adminRoles || {}
          
          if (adminIds[currentUser?.uid]) {
            const userRole = adminRoles[currentUser?.uid] || 'admin'
            const canManageAdmins = userRole === 'organization_admin' || userRole === 'org_admin'
            
            adminOrgs.push({
              id: orgId,
              name: orgData.name,
              userRole,
              canManageAdmins
            })
            
            console.log(`✅ Found admin organization: ${orgData.name} (role: ${userRole})`)
          }
        }
        
        console.log('🔍 Total admin organizations found:', adminOrgs.length)
        setAdminOrganizations(adminOrgs)
        setIsOrgAdmin(adminOrgs.length > 0)
        return adminOrgs
      }
    } catch (error) {
      console.error('❌ Error loading admin organizations:', error)
      return []
    }
  }

  // Get date range based on time period
  const getDateRange = (period) => {
    const now = new Date()
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    
    switch (period) {
      case 'day':
        return {
          start: Timestamp.fromDate(startOfDay),
          end: Timestamp.fromDate(now),
          label: 'Today'
        }
      case 'week':
        const startOfWeek = new Date(startOfDay)
        startOfWeek.setDate(startOfDay.getDate() - startOfDay.getDay())
        return {
          start: Timestamp.fromDate(startOfWeek),
          end: Timestamp.fromDate(now),
          label: 'This Week'
        }
      case 'month':
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
        return {
          start: Timestamp.fromDate(startOfMonth),
          end: Timestamp.fromDate(now),
          label: 'This Month'
        }
      case 'year':
        const startOfYear = new Date(now.getFullYear(), 0, 1)
        return {
          start: Timestamp.fromDate(startOfYear),
          end: Timestamp.fromDate(now),
          label: 'This Year'
        }
      case 'total':
      default:
        return {
          start: null,
          end: null,
          label: 'All Time'
        }
    }
  }

  // Fetch platform-wide statistics with time period filter
  const fetchPlatformStats = async (period = timePeriod) => {
    if (!isPlatformAdmin) return
    
    setStatsLoading(true)
    try {
      console.log(`🔧 Fetching platform-wide statistics for ${period}...`)
      
      const dateRange = getDateRange(period)
      console.log('📅 Date range:', dateRange)
      
      // Get total users (not time-filtered as users are created once)
      const usersQuery = query(collection(db, 'users'))
      const usersSnapshot = await getDocs(usersQuery)
      const totalUsers = usersSnapshot.size
      
      // Get total organizations (not time-filtered as orgs are created once)
      const orgsQuery = query(collection(db, 'organizations'))
      const orgsSnapshot = await getDocs(orgsQuery)
      const totalOrganizations = orgsSnapshot.size
      
      // Get pending requests (not time-filtered as they're current status)
      const requestsQuery = query(
        collection(db, 'organizationRequests'),
        where('status', '==', 'pending')
      )
      const requestsSnapshot = await getDocs(requestsQuery)
      const pendingRequests = requestsSnapshot.size
      
      // Get time-filtered alerts and emails across all organizations
      let totalAlerts = 0
      let totalEmails = 0
      
      console.log(`🔧 Processing ${orgsSnapshot.size} organizations for alerts and emails...`)
      
      for (const orgDoc of orgsSnapshot.docs) {
        const orgId = orgDoc.id
        console.log(`🔧 Processing organization: ${orgId}`)
        
        try {
          // Count alerts for this organization with time filter
          let alertsQuery = query(collection(db, 'organizations', orgId, 'alerts'))
          
          if (dateRange.start && dateRange.end) {
            try {
              alertsQuery = query(
                collection(db, 'organizations', orgId, 'alerts'),
                where('createdAt', '>=', dateRange.start),
                where('createdAt', '<=', dateRange.end)
              )
            } catch (queryError) {
              console.log(`   ⚠️ Time-based query failed for ${orgId} alerts, using all alerts:`, queryError.message)
              // Fallback to all alerts if time-based query fails
              alertsQuery = query(collection(db, 'organizations', orgId, 'alerts'))
            }
          }
          
          const alertsSnapshot = await getDocs(alertsQuery)
          const orgAlerts = alertsSnapshot.size
          totalAlerts += orgAlerts
          console.log(`   📊 ${orgId}: ${orgAlerts} alerts`)
          
        } catch (error) {
          console.log(`   ⚠️ Error getting alerts for ${orgId}:`, error.message)
          // Continue with other organizations
        }
        
        // Count emails for this organization with time filter
        try {
          let emailsQuery = query(collection(db, 'organizations', orgId, 'emails'))
          
          if (dateRange.start && dateRange.end) {
            try {
              emailsQuery = query(
                collection(db, 'organizations', orgId, 'emails'),
                where('createdAt', '>=', dateRange.start),
                where('createdAt', '<=', dateRange.end)
              )
            } catch (queryError) {
              console.log(`   ⚠️ Time-based query failed for ${orgId} emails, using all emails:`, queryError.message)
              // Fallback to all emails if time-based query fails
              emailsQuery = query(collection(db, 'organizations', orgId, 'emails'))
            }
          }
          
          const emailsSnapshot = await getDocs(emailsQuery)
          const orgEmails = emailsSnapshot.size
          totalEmails += orgEmails
          console.log(`   📧 ${orgId}: ${orgEmails} emails`)
          
        } catch (error) {
          console.log(`   ⚠️ Error getting emails for ${orgId}:`, error.message)
          // Continue with other organizations
        }
      }
      
      // Get email statistics from email tracking
      let emailStats = {
        totalEmails: 0,
        alertEmails: 0,
        organizationRequestEmails: 0,
        testEmails: 0,
        otherEmails: 0
      }
      
      try {
        const emailStatsData = await emailStatsService.getEmailStatsByPeriod(period)
        if (Array.isArray(emailStatsData)) {
          // Multiple months data
          emailStats = emailStatsData.reduce((acc, monthData) => ({
            totalEmails: acc.totalEmails + (monthData.totalEmails || 0),
            alertEmails: acc.alertEmails + (monthData.alertEmails || 0),
            organizationRequestEmails: acc.organizationRequestEmails + (monthData.organizationRequestEmails || 0),
            testEmails: acc.testEmails + (monthData.testEmails || 0),
            otherEmails: acc.otherEmails + (monthData.otherEmails || 0)
          }), { totalEmails: 0, alertEmails: 0, organizationRequestEmails: 0, testEmails: 0, otherEmails: 0 })
        } else {
          // Single period data
          emailStats = {
            totalEmails: emailStatsData.totalEmails || 0,
            alertEmails: emailStatsData.alertEmails || 0,
            organizationRequestEmails: emailStatsData.organizationRequestEmails || 0,
            testEmails: emailStatsData.testEmails || 0,
            otherEmails: emailStatsData.otherEmails || 0
          }
        }
        console.log(`✅ Email stats for period ${period}:`, emailStats)
      } catch (emailStatsError) {
        console.warn('⚠️ Email stats query failed:', emailStatsError.message)
        // Keep default email stats
      }
      
      const stats = {
        totalAlerts,
        totalEmails,
        totalUsers,
        totalOrganizations,
        pendingRequests,
        emailStats,
        timePeriod: period,
        dateRange: dateRange.label
      }
      
      setPlatformStats(stats)
      console.log(`✅ Platform stats loaded for ${period}:`, stats)
      
    } catch (error) {
      console.error('❌ Error fetching platform stats:', error)
      toast.error('Failed to load platform statistics')
    } finally {
      setStatsLoading(false)
    }
  }

  // Handle time period change
  const handleTimePeriodChange = (newPeriod) => {
    setTimePeriod(newPeriod)
    fetchPlatformStats(newPeriod)
  }

  // Fetch only pending organization requests for platform admin
  const fetchOrganizationRequests = async () => {
    if (!isPlatformAdmin) return
    
    setRequestsLoading(true)
    try {
      console.log('🔧 Fetching organization requests...')
      
      // First try with the composite query
      let requestsQuery = query(
        collection(db, 'organizationRequests'),
        where('status', '==', 'pending'),
        orderBy('createdAt', 'desc')
      )
      
      let requestsSnapshot
      try {
        requestsSnapshot = await getDocs(requestsQuery)
        console.log('✅ Successfully queried with composite index')
      } catch (queryError) {
        console.log('⚠️ Composite query failed, trying without orderBy:', queryError.message)
        
        // Fallback: query without orderBy and sort client-side
        requestsQuery = query(
          collection(db, 'organizationRequests'),
          where('status', '==', 'pending')
        )
        requestsSnapshot = await getDocs(requestsQuery)
        console.log('✅ Fallback query successful')
      }
      
      const requestsData = requestsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      
      // Sort by createdAt if we used the fallback query
      if (requestsData.length > 0 && requestsData[0].createdAt) {
        requestsData.sort((a, b) => {
          const aTime = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt)
          const bTime = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt)
          return bTime - aTime // Descending order
        })
      }
      
      setOrganizationRequests(requestsData)
      console.log('✅ Loaded pending organization requests:', requestsData.length)
    } catch (error) {
      console.error('❌ Error fetching organization requests:', error)
      console.error('❌ Error details:', error.message)
      toast.error('Failed to load organization requests: ' + error.message)
    } finally {
      setRequestsLoading(false)
    }
  }

  // Approve organization request
  const approveRequest = async (requestId) => {
    try {
      // Get the request data first
      const requestRef = doc(db, 'organizationRequests', requestId)
      const requestDoc = await getDoc(requestRef)
      
      if (!requestDoc.exists()) {
        throw new Error('Request not found')
      }
      
      const requestData = { id: requestDoc.id, ...requestDoc.data() }
      
      // Use the organization approval service to create the organization
      const organizationApprovalService = (await import('../services/organizationApprovalService')).default
      await organizationApprovalService.approveOrganizationRequest(requestData, currentUser)
      
      toast.success('Organization request approved and organization created!')
      fetchOrganizationRequests()
    } catch (error) {
      console.error('Error approving request:', error)
      toast.error('Failed to approve request: ' + error.message)
    }
  }

  // Reject organization request
  const rejectRequest = async (requestId) => {
    try {
      const requestRef = doc(db, 'organizationRequests', requestId)
      await updateDoc(requestRef, {
        status: 'rejected',
        updatedAt: serverTimestamp()
      })
      
      toast.success('Organization request rejected!')
      fetchOrganizationRequests()
    } catch (error) {
      console.error('Error rejecting request:', error)
      toast.error('Failed to reject request')
    }
  }

  // Export platform data
  const exportPlatformData = () => {
    const data = {
      organizations: organizations || [],
      organizationRequests: organizationRequests || [],
      adminOrganizations: adminOrganizations || [],
      exportDate: new Date().toISOString(),
      platformVersion: '1.0.0'
    }
    
    const filename = `platform_data_${new Date().toISOString().split('T')[0]}.json`
    const jsonStr = JSON.stringify(data, null, 2)
    const blob = new Blob([jsonStr], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    
    toast.success('Platform data exported!')
  }

  // Check platform admin status on mount
  useEffect(() => {
    if (currentUser) {
      checkPlatformAdminStatus()
    }
  }, [currentUser])

  // Load admin organizations when user changes
  useEffect(() => {
    if (currentUser && isPlatformAdmin) {
      loadAdminOrganizationsFromFirestore()
    }
  }, [currentUser, isPlatformAdmin])

  // Fetch organization requests and platform stats when platform admin status changes
  useEffect(() => {
    if (isPlatformAdmin) {
      fetchOrganizationRequests()
      fetchPlatformStats()
    }
  }, [isPlatformAdmin])

  // Auto-fix platform admin access
  useEffect(() => {
    const handlePlatformAdminAccess = async () => {
      if (!currentUser?.uid || !userProfile) return
      
      const isPlatformAdminUser = platformAdminUIDs.includes(currentUser.uid)
      
      console.log('🔧 Platform admin access check:', {
        isPlatformAdminUser,
        userProfileIsOrgAdmin: userProfile.isOrganizationAdmin,
        adminOrganizationsLength: adminOrganizations.length
      })
      
      if (isPlatformAdminUser && userProfile.isOrganizationAdmin && adminOrganizations.length === 0) {
        console.log('🔧 Platform admin detected with org admin status but no admin organizations - fixing...')
        await grantPlatformAdminOrgAccess()
      }
    }
    
    handlePlatformAdminAccess()
  }, [currentUser, userProfile, adminOrganizations.length])

  // Redirect if not platform admin
  if (currentUser && !isPlatformAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Crown className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You need platform creator access to view this page.</p>
        </div>
      </div>
    )
  }

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Creator Header */}
        <div className="mb-8 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg p-6 text-white">
          <div className="flex items-center">
            <Crown className="h-8 w-8 mr-3" />
            <div>
              <h1 className="text-3xl font-bold">Platform Creator Dashboard</h1>
              <p className="text-purple-100">You created this platform - you have full control over everything</p>
            </div>
          </div>
        </div>

        <div className="space-y-8">
          {/* Time Period Filter */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Platform Analytics</h3>
                <p className="text-sm text-gray-500">
                  Showing data for: <span className="font-medium text-gray-900">{platformStats.dateRange || 'All Time'}</span>
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <label htmlFor="timePeriod" className="text-sm font-medium text-gray-700">
                  Time Period:
                </label>
                <select
                  id="timePeriod"
                  value={timePeriod}
                  onChange={(e) => handleTimePeriodChange(e.target.value)}
                  disabled={statsLoading}
                  className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                >
                  <option value="day">Today</option>
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                  <option value="year">This Year</option>
                  <option value="total">All Time</option>
                </select>
              </div>
            </div>
          </div>

          {/* Platform Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center">
                <Building className="h-8 w-8 text-purple-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Total Organizations</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {statsLoading ? '...' : platformStats.totalOrganizations}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center">
                <Users className="h-8 w-8 text-blue-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Total Users</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {statsLoading ? '...' : platformStats.totalUsers}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center">
                <Activity className="h-8 w-8 text-green-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">
                    {timePeriod === 'total' ? 'Total Alerts' : `${platformStats.dateRange} Alerts`}
                  </p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {statsLoading ? '...' : platformStats.totalAlerts}
                  </p>
                  {timePeriod !== 'total' && (
                    <p className="text-xs text-gray-500">Created in this period</p>
                  )}
                </div>
              </div>
            </div>
            
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center">
                <BarChart3 className="h-8 w-8 text-orange-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">
                    {timePeriod === 'total' ? 'Total Emails' : `${platformStats.dateRange} Emails`}
                  </p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {statsLoading ? '...' : platformStats.totalEmails}
                  </p>
                  {timePeriod !== 'total' && (
                    <p className="text-xs text-gray-500">Sent in this period</p>
                  )}
                </div>
              </div>
            </div>
            
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center">
                <FileText className="h-8 w-8 text-red-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Pending Requests</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {statsLoading ? '...' : platformStats.pendingRequests}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Email Statistics Breakdown */}
          <div className="mt-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Email Statistics Breakdown</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="bg-white shadow rounded-lg p-4">
                <div className="flex items-center">
                  <FileText className="h-6 w-6 text-blue-600" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-500">Alert Emails</p>
                    <p className="text-xl font-semibold text-gray-900">
                      {statsLoading ? '...' : platformStats.emailStats?.alertEmails || 0}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white shadow rounded-lg p-4">
                <div className="flex items-center">
                  <Building className="h-6 w-6 text-green-600" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-500">Org Request Emails</p>
                    <p className="text-xl font-semibold text-gray-900">
                      {statsLoading ? '...' : platformStats.emailStats?.organizationRequestEmails || 0}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white shadow rounded-lg p-4">
                <div className="flex items-center">
                  <Zap className="h-6 w-6 text-yellow-600" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-500">Test Emails</p>
                    <p className="text-xl font-semibold text-gray-900">
                      {statsLoading ? '...' : platformStats.emailStats?.testEmails || 0}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white shadow rounded-lg p-4">
                <div className="flex items-center">
                  <Settings className="h-6 w-6 text-gray-600" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-500">Other Emails</p>
                    <p className="text-xl font-semibold text-gray-900">
                      {statsLoading ? '...' : platformStats.emailStats?.otherEmails || 0}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white shadow rounded-lg p-4">
                <div className="flex items-center">
                  <BarChart3 className="h-6 w-6 text-purple-600" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-500">Total Tracked</p>
                    <p className="text-xl font-semibold text-gray-900">
                      {statsLoading ? '...' : platformStats.emailStats?.totalEmails || 0}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Refresh Stats Button */}
          <div className="flex justify-end">
            <button
              onClick={() => fetchPlatformStats(timePeriod)}
              disabled={statsLoading}
              className="btn-secondary flex items-center"
            >
              {statsLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                  Refreshing...
                </>
              ) : (
                <>
                  <Activity className="h-4 w-4 mr-2" />
                  Refresh {platformStats.dateRange || 'Statistics'}
                </>
              )}
            </button>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <Zap className="h-5 w-5 mr-2 text-yellow-600" />
                Platform Management
              </h3>
              
              <div className="space-y-3">
                <button
                  onClick={grantPlatformAdminOrgAccess}
                  className="w-full btn-primary flex items-center justify-center"
                >
                  <Crown className="h-4 w-4 mr-2" />
                  Grant Platform Admin Access
                </button>
                
                <button
                  onClick={() => {
                    console.clear()
                    console.log('🔍 CURRENT STATUS CHECK:')
                    console.log('User ID:', currentUser?.uid)
                    console.log('User Profile:', userProfile)
                    console.log('Admin Organizations:', adminOrganizations)
                    console.log('Is Platform Admin:', isPlatformAdmin)
                    console.log('Is Org Admin:', isOrgAdmin)
                    
                    // Check organization data
                    const checkOrgData = async () => {
                      try {
                        const orgsQuery = query(collection(db, 'organizations'))
                        const orgsSnapshot = await getDocs(orgsQuery)
                        
                        for (const orgDoc of orgsSnapshot.docs) {
                          const orgData = orgDoc.data()
                          console.log(`🏢 Organization: ${orgData.name}`)
                          console.log(`   Admin IDs:`, orgData.adminIds)
                          console.log(`   Admin Roles:`, orgData.adminRoles)
                          console.log(`   Contains your ID:`, orgData.adminIds?.[currentUser?.uid] === true)
                        }
                      } catch (error) {
                        console.error('Error checking org data:', error)
                      }
                    }
                    
                    checkOrgData()
                    toast.success('Status logged to console - check console now!')
                  }}
                  className="w-full btn-secondary flex items-center justify-center"
                >
                  <Activity className="h-4 w-4 mr-2" />
                  Check Current Status
                </button>
                
                <button
                  onClick={() => setShowSpecialAccessModal(true)}
                  className="w-full btn-secondary flex items-center justify-center"
                >
                  <Shield className="h-4 w-4 mr-2" />
                  Manage Special Access
                </button>
                
                <button
                  onClick={() => window.open('/org-requests', '_blank')}
                  className="w-full btn-secondary flex items-center justify-center"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Review Organization Requests
                </button>
                
                <button
                  onClick={() => window.open('/analytics', '_blank')}
                  className="w-full btn-secondary flex items-center justify-center"
                >
                  <BarChart3 className="h-4 w-4 mr-2" />
                  View Platform Analytics
                </button>
              </div>
            </div>

            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <Settings className="h-5 w-5 mr-2 text-blue-600" />
                System Tools
              </h3>
              
              <div className="space-y-3">
                <button
                  onClick={exportPlatformData}
                  className="w-full btn-secondary flex items-center justify-center"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export Platform Data
                </button>
                
                <button
                  onClick={() => {
                    console.log('System maintenance')
                    toast.success('Maintenance mode feature coming soon!')
                  }}
                  className="w-full btn-secondary flex items-center justify-center"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  System Maintenance
                </button>
                
                <button
                  onClick={() => {
                    console.log('User management')
                    toast.success('User management feature coming soon!')
                  }}
                  className="w-full btn-secondary flex items-center justify-center"
                >
                  <Users className="h-4 w-4 mr-2" />
                  User Management
                </button>
                
                <button
                  onClick={() => {
                    console.log('System logs')
                    toast.success('System logs feature coming soon!')
                  }}
                  className="w-full btn-secondary flex items-center justify-center"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  System Logs
                </button>
              </div>
            </div>
          </div>

          {/* Organization Requests */}
          {organizationRequests.length > 0 && (
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <FileText className="h-5 w-5 mr-2 text-blue-600" />
                Recent Organization Requests
              </h3>
              
              <div className="space-y-4">
                {organizationRequests.slice(0, 5).map((request) => (
                  <div key={request.id} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900">{request.organizationName}</h4>
                        <p className="text-sm text-gray-500">{request.adminEmail}</p>
                        <p className="text-xs text-gray-400">
                          {request.createdAt?.toDate?.()?.toLocaleDateString() || 'Unknown date'}
                        </p>
                      </div>
                      <div className="flex space-x-2">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          request.status === 'approved' ? 'bg-green-100 text-green-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {request.status}
                        </span>
                        {request.status === 'pending' && (
                          <>
                            <button
                              onClick={() => approveRequest(request.id)}
                              className="btn-primary text-xs px-3 py-1"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => rejectRequest(request.id)}
                              className="btn-danger text-xs px-3 py-1"
                            >
                              Reject
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* System Information */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <Activity className="h-5 w-5 mr-2 text-blue-600" />
              System Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-1">
                <p className="text-sm text-gray-500">Platform Version</p>
                <p className="text-sm font-medium">1.0.0</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-gray-500">Last Updated</p>
                <p className="text-sm font-medium">{new Date().toLocaleDateString()}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-gray-500">Environment</p>
                <p className="text-sm font-medium">Production</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-gray-500">Database</p>
                <p className="text-sm font-medium">Firestore</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Special Access Manager Modal */}
      {showSpecialAccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">Special Access Manager</h3>
              <button 
                onClick={() => setShowSpecialAccessModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              <SpecialAccessManager />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
